/**
 * A dev-only stand-in for the Server surface, so the Web surface is walkable
 * end to end before the real API is up. It speaks exactly the routes in
 * product/api.json and returns exactly the schemas in product/data/, including
 * error.schema.json bodies.
 *
 * It installs itself only when the real Server does not answer (see
 * installMockIfServerIsDown in ./index.ts) and never ships: import.meta.env.DEV
 * gates every entry point.
 */
import type {
  Account,
  ApiError,
  AuthIdentity,
  AuthMethods,
  BankConnection,
  DebtPlan,
  Money,
  Progress,
  Recommendation,
  RecurringSeries,
  Statement,
  Transaction,
} from '@/generated/types'
import type { CategoryId } from '@/generated/types'
import { setTransport, type ConfirmImportResponse, type UserSettings } from '../api'
import { bridge } from '../bridge'
import {
  buildPlan,
  deriveCashFlow,
  deriveDebt,
  deriveFees,
  deriveProgress,
  deriveRecommendations,
  deriveRecurring,
  deriveScore,
} from './analysis'
import { CURRENCY, makeDataset, money, uuid, type Dataset, type DatasetSize } from './dataset'
import {
  generationModeFromUrl,
  generationStatus,
  withProgressNarrative,
  withRecommendationProse,
  withScoreNarrative,
  type GenerationMode,
} from './generation'

interface MockState {
  size: DatasetSize
  data: Dataset
  settings: UserSettings
  /** Statements uploaded during this session, before/after confirmation. */
  pending: Map<string, { statement: Statement; transactions: Transaction[]; password?: string }>
  recommendationStatus: Map<string, Recommendation['status']>
  recurringOverride: Map<string, Partial<RecurringSeries>>
  transactionOverride: Map<string, Partial<Transaction>>
  savedPlan: DebtPlan | null
  authenticated: boolean
  identities: AuthIdentity[]
  /** Live bank connections. Disconnected ones are removed, never flagged. */
  connections: BankConnection[]
  /** From `?generation=`; `unavailable` is the no-key posture, and the default. */
  generation: GenerationMode
  /** Reads of /analysis/status so far, so `pending` can settle rather than hang. */
  statusPolls: number
}

/**
 * How the fake provider behaves, from `?plaid=`. `off` is the no-credentials
 * posture the SPEC requires every surface to survive; the others let linkError
 * be reached without a real bank refusing a real connection.
 */
type PlaidMode = 'on' | 'off' | 'cancel' | 'fail'

function plaidModeFromUrl(): PlaidMode {
  const requested = new URLSearchParams(location.search).get('plaid')
  if (requested === 'off' || requested === 'cancel' || requested === 'fail') return requested
  return 'on'
}

let state: MockState | null = null

function datasetSizeFromUrl(): DatasetSize {
  const requested = new URLSearchParams(location.search).get('dataset')
  if (requested === 'empty' || requested === 'thin' || requested === 'full') return requested
  return 'full'
}

function init(): MockState {
  const size = datasetSizeFromUrl()
  const data = makeDataset(size)
  return {
    size,
    data,
    settings: {
      email: 'you@example.com',
      currency: CURRENCY,
      emergencyFundTarget: data.emergencyFundTarget,
      notifications: { newAnalysis: true, priceIncrease: true, fee: false },
    },
    pending: new Map(),
    recommendationStatus: new Map(),
    recurringOverride: new Map(),
    transactionOverride: new Map(),
    savedPlan: null,
    authenticated:
      localStorage.getItem('dimeroot.session.present') !== null ||
      localStorage.getItem('fintech.session.email') !== null,
    identities: [
      {
        id: uuid('identity:google'),
        provider: 'google',
        connectedAt: '2026-01-01T00:00:00.000Z',
        lastUsedAt: '2026-01-01T00:00:00.000Z',
        canUnlink: true,
      },
    ],
    connections: [],
    generation: generationModeFromUrl(),
    statusPolls: 0,
  }
}

function store(): MockState {
  if (!state) state = init()
  return state
}

// --------------------------------------------------------------- HTTP helpers

const json = (body: unknown, status = 200) =>
  new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } })

const noContent = () => new Response(null, { status: 204 })

const STATUS: Record<ApiError['code'], number> = {
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  validationFailed: 422,
  conflict: 409,
  unsupportedFormat: 415,
  fileTooLarge: 413,
  passwordProtected: 422,
  parseFailed: 422,
  noTransactionsFound: 422,
  duplicateStatement: 409,
  insufficientData: 200,
  rateLimited: 429,
  authenticationFailed: 422,
  authTransactionExpired: 410,
  authReplay: 409,
  // 410, matching Server/src/lib/errors.ts. The offer is gone, not unauthorized —
  // a stub that answered 401 here would exercise a status the origin never sends.
  betaOfferExpired: 410,
  emailCollision: 409,
  identityCollision: 409,
  providerSlotOccupied: 409,
  reauthenticationRequired: 403,
  lastSignInMethod: 409,
  providerUnavailable: 503,
  serverError: 500,
  // The four the RELAY produces. The origin cannot report that it was
  // unreachable, so this stub can only fake them — see `edgeModeFromUrl`.
  tunnelUnavailable: 503,
  originUnreachable: 502,
  originTimeout: 504,
  tunnelBusy: 503,
}

const fail = (code: ApiError['code'], copyKey: string, extra: Partial<ApiError> = {}) =>
  json({ code, copyKey, retryable: code === 'serverError', ...extra } satisfies ApiError, STATUS[code])

/**
 * The edge is not part of this stub — it sits in FRONT of the real Server, and
 * the mock stands in for the Server. But serviceUnavailable is unreachable in
 * dev without it, so `?edge=` fakes the relay refusing a request, exactly as
 * `?plaid=` and `?generation=` fake the other two things this surface cannot
 * produce locally.
 *
 *   ?edge=tunnelUnavailable   nothing was connected; safe to retry
 *   ?edge=tunnelBusy          refused under load before handoff; safe to retry
 *   ?edge=originUnreachable   handed over and lost; MAY have been applied
 *   ?edge=originTimeout       handed over and never answered; MAY have been applied
 *   ?edge=recover             refuses once per path, then answers — walks Retry
 */
const EDGE_COPY: Record<string, string> = {
  tunnelUnavailable: 'error.origin.unreachable',
  originUnreachable: 'error.origin.unreachable',
  originTimeout: 'error.origin.timeout',
  tunnelBusy: 'error.origin.busy',
}

/** Only the two refused BEFORE handoff are known not to have been applied. */
const SAFE_TO_RETRY = new Set(['tunnelUnavailable', 'tunnelBusy'])

function edgeModeFromUrl(): string | null {
  const requested = new URLSearchParams(location.search).get('edge')
  if (!requested) return null
  return requested === 'recover' || requested in EDGE_COPY ? requested : null
}

/** Paths already refused once under `?edge=recover`, so Retry can succeed. */
const refusedOnce = new Set<string>()

function edgeRefusal(path: string): Response | null {
  const mode = edgeModeFromUrl()
  if (!mode) return null
  if (mode === 'recover') {
    if (refusedOnce.has(path)) return null
    refusedOnce.add(path)
    return json(
      { code: 'tunnelUnavailable', copyKey: EDGE_COPY.tunnelUnavailable, retryable: true } satisfies ApiError,
      503,
    )
  }
  return json(
    {
      code: mode as ApiError['code'],
      copyKey: EDGE_COPY[mode],
      retryable: SAFE_TO_RETRY.has(mode),
    } satisfies ApiError,
    STATUS[mode as ApiError['code']],
  )
}

// -------------------------------------------------------------------- derived

/** Analysis is recomputed on read — cheap at this data size, always consistent. */
function analysis(s: MockState) {
  const data = withOverrides(s)
  const cashFlow = deriveCashFlow(data, 12)
  const recurring = deriveRecurring(data).map((series) => ({
    ...series,
    ...(s.recurringOverride.get(series.id) ?? {}),
  }))
  const fees = deriveFees(data, 12)
  const score = deriveScore(data, cashFlow, fees, recurring)
  const recommendations = deriveRecommendations(data, cashFlow, fees, recurring).map((rec) => ({
    ...rec,
    status: s.recommendationStatus.get(rec.id) ?? rec.status,
    statusChangedAt: s.recommendationStatus.get(rec.id) ? score.computedAt : null,
  }))
  score.topLevers = recommendations.slice(0, 3).map((r) => r.id)
  return { data, cashFlow, recurring, fees, score, recommendations }
}

function withOverrides(s: MockState): Dataset {
  if (s.transactionOverride.size === 0) return s.data
  return {
    ...s.data,
    transactions: s.data.transactions.map((tx) => ({ ...tx, ...(s.transactionOverride.get(tx.id) ?? {}) })),
  }
}

/**
 * The `users` row password sign-in resolves to, matching the Server's
 * AUTH_LOCAL_EMAIL default. The self-hosted box has one owner and no mail
 * provider, so this address is a local identity, not a deliverable one.
 */
const AUTH_LOCAL_EMAIL = 'owner@local'

// ------------------------------------------------------------- upload parsing

const MAX_BYTES = 25 * 1024 * 1024

function formatOf(name: string): Statement['format'] | null {
  const lower = name.toLowerCase()
  if (lower.endsWith('.pdf')) return 'pdf'
  if (lower.endsWith('.csv')) return 'csv'
  return null
}

/**
 * Fabricates a parse result from the uploaded file's name so the upload ->
 * importReview -> dashboard flow can be exercised without a parser. Files with
 * "locked" in the name come back password protected, so the locked state is
 * reachable on demand.
 */
function parseUpload(s: MockState, file: File, format: Statement['format']): Statement {
  const id = uuid(`upload:${file.name}:${file.size}:${s.pending.size}`)
  const lower = file.name.toLowerCase()
  const now = new Date()
  const period = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const periodKey = `${period.getFullYear()}-${String(period.getMonth() + 1).padStart(2, '0')}`

  const statement: Statement = {
    id,
    accountId: null,
    fileName: file.name,
    fileSizeBytes: file.size,
    format,
    status: 'parsing',
    failureReason: null,
    periodStart: `${periodKey}-01`,
    periodEnd: `${periodKey}-${new Date(period.getFullYear(), period.getMonth() + 1, 0).getDate()}`,
    openingBalance: money(1_284_00),
    closingBalance: money(428_11),
    detected: {
      institution: 'Meridian Bank',
      accountType: 'checking',
      mask: '1042',
      currency: CURRENCY,
      confidence: 0.94,
    },
    transactionCount: 0,
    skippedRowCount: lower.includes('messy') ? 3 : 0,
    duplicateCount: 0,
    balanceReconciles: !lower.includes('messy'),
    uploadedAt: now.toISOString(),
    importedAt: null,
  }

  const transactions: Transaction[] = [
    ['Northwind Labs', 'NORTHWIND LABS DIRECT DEP', 'income' as CategoryId, 4_412_00, 1],
    ['Meadow Ridge Apartments', 'MEADOW RIDGE APTS RENT', 'housing' as CategoryId, -1_845_00, 1],
    ['Greenhill Market', 'GREENHILL MARKET #221', 'groceries' as CategoryId, -128_40, 4],
    ['Daybreak Coffee', 'DAYBREAK COFFEE CO', 'dining' as CategoryId, -6_75, 6],
    ['Citywide Power', 'CITYWIDE POWER EBILL', 'utilities' as CategoryId, -132_75, 18],
    ['Netflix', 'NETFLIX.COM 866-579-7172 CA', 'subscriptions' as CategoryId, -19_99, 8],
    ['Monthly service fee', 'MONTHLY MAINTENANCE FEE', 'fees' as CategoryId, -12_00, 28],
  ].map(([merchant, description, category, amount, day], i) => ({
    id: uuid(`preview:${id}:${i}`),
    accountId: '',
    statementId: id,
    date: `${periodKey}-${String(day).padStart(2, '0')}`,
    postedDate: `${periodKey}-${String(day).padStart(2, '0')}`,
    amount: money(amount as number),
    description: description as string,
    merchant: merchant as string,
    merchantKey: (merchant as string).toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    category: category as CategoryId,
    categorySource: 'rule' as const,
    categoryConfidence: 0.9,
    direction: (amount as number) >= 0 ? ('inflow' as const) : ('outflow' as const),
    isRecurring: false,
    recurringSeriesId: null,
    isFee: category === 'fees',
    feeKind: category === 'fees' ? ('monthlyMaintenance' as const) : null,
    isTransfer: false,
    transferPairId: null,
    isDuplicateOf: null,
    notes: null,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  }))

  statement.transactionCount = transactions.length
  statement.duplicateCount = lower.includes('again') ? 2 : 0

  if (lower.includes('locked')) {
    statement.status = 'failed'
    statement.failureReason = 'passwordProtected'
  }
  if (lower.includes('blank')) {
    statement.status = 'failed'
    statement.failureReason = 'noTransactionsFound'
  }

  s.pending.set(id, { statement, transactions, password: lower.includes('locked') ? 'open' : undefined })
  return statement
}

// ------------------------------------------------------------------- handlers

type Handler = (ctx: {
  s: MockState
  params: string[]
  url: URL
  body: () => Promise<Record<string, unknown>>
  form: () => Promise<FormData>
}) => Promise<Response> | Response

interface Route {
  method: string
  pattern: RegExp
  handler: Handler
}

const routes: Route[] = []
const on = (method: string, path: string, handler: Handler) => {
  routes.push({
    method,
    pattern: new RegExp(`^${path.replace(/\{[^}]+\}/g, '([^/]+)')}$`),
    handler,
  })
}

/**
 * Which sign-in methods the fake deployment advertises, from `?auth=`. This
 * mirrors the Server's env gating. Google is first and password is last.
 */
function advertisedMethods(): AuthMethods['methods'] {
  switch (new URLSearchParams(location.search).get('auth')) {
    case 'password':
      return [{ id: 'password', presentation: 'breakGlass' }]
    case 'google':
      return [{ id: 'google', presentation: 'primary' }]
    case 'both':
      return [
        { id: 'google', presentation: 'primary' },
        { id: 'password', presentation: 'breakGlass' },
      ]
    default:
      return [
        { id: 'google', presentation: 'primary' },
        { id: 'password', presentation: 'breakGlass' },
      ]
  }
}

on('GET', '/auth/methods', ({ url }) => {
  if (url.searchParams.get('surface') !== 'web') return fail('validationFailed', 'error.generic')
  return json({ surface: 'web', methods: advertisedMethods() } satisfies AuthMethods)
})

on('POST', '/auth/code', () => new Response(null, { status: 202 }))

on('POST', '/auth/verify', async ({ s, body }) => {
  const { email, code } = (await body()) as { email?: string; code?: string }
  if (!email || !/.+@.+\..+/.test(email)) {
    return fail('validationFailed', 'signIn.error.email', { field: 'email' })
  }
  if (!code || code.length !== 6) return fail('validationFailed', 'signIn.error.code', { field: 'code' })
  s.authenticated = true
  s.settings = { ...s.settings, email }
  return json({ token: 'mock-token', user: { id: uuid(`user:${email}`), email, currency: CURRENCY } })
})

/**
 * Static credentials. A deploy that does not offer `password` has not mounted
 * the route at all, so the mock 404s rather than checking anything — that is
 * the response the real Server gives with AUTH_PASSWORD_ENABLED unset.
 *
 * There is no fake credential to memorise: the real check is a scrypt hash in
 * the environment, and the mock only needs both halves to be plausible. A
 * short password is the way to walk the `error` state, and a wrong username
 * and a wrong password return the identical body with field omitted.
 */
on('POST', '/auth/password', async ({ s, body }) => {
  if (!advertisedMethods().some((method) => method.id === 'password')) return fail('notFound', 'error.notFound')
  const { username, password } = (await body()) as { username?: string; password?: string }
  if (!username || !password || password.length < 8) {
    return fail('validationFailed', 'signIn.error.credentials', { field: null })
  }
  const email = AUTH_LOCAL_EMAIL
  s.authenticated = true
  s.settings = { ...s.settings, email }
  return json({ token: 'mock-token', user: { id: uuid(`user:${email}`), email, currency: CURRENCY } })
})

on('POST', '/auth/reauth/password', async ({ body }) => {
  const { username, password } = (await body()) as { username?: string; password?: string }
  if (!username || !password || password.length < 8) {
    return fail('authenticationFailed', 'reauthenticate.error.mismatch', { field: null })
  }
  return noContent()
})

on('GET', '/auth/identities', ({ s }) => json(s.identities))

on('DELETE', '/auth/identities/{id}', ({ s, params }) => {
  const identity = s.identities.find((item) => item.id === params[0])
  if (!identity) return fail('notFound', 'error.notFound')
  if (!identity.canUnlink) return fail('lastSignInMethod', 'signInMethods.error.lastMethod')
  s.identities = s.identities.filter((item) => item.id !== identity.id)
  return noContent()
})

on('GET', '/beta-notifications', () => {
  const beta = new URLSearchParams(location.search).get('beta')
  if (beta === 'expired') return fail('betaOfferExpired', 'betaAccess.expired.body', { retryable: false })
  if (beta === 'error') return fail('serverError', 'error.server')
  return json({ status: 'betaAccessRequired', provider: 'google', surface: 'web', expiresAt: '2026-08-14T23:59:59.000Z' })
})

on('POST', '/beta-notifications', async ({ body }) => {
  const value = await body()
  if (value.consent !== true || Object.keys(value).length !== 1) {
    return fail('validationFailed', 'error.generic', { retryable: false })
  }
  const status = new URLSearchParams(location.search).get('beta') === 'already' ? 'alreadyJoined' : 'accepted'
  return json({ status })
})

on('DELETE', '/beta-notifications', () => noContent())

on('POST', '/auth/signout', ({ s }) => {
  s.authenticated = false
  return noContent()
})

on('POST', '/statements', async ({ s, form }) => {
  const data = await form()
  const file = data.get('file')
  if (!(file instanceof File)) return fail('validationFailed', 'upload.error.failed')
  if (file.size > MAX_BYTES) return fail('fileTooLarge', 'upload.error.tooLarge')
  const format = formatOf(file.name)
  if (!format) return fail('unsupportedFormat', 'upload.error.unsupported')
  return json(parseUpload(s, file, format))
})

on('GET', '/statements/{id}', ({ s, params }) => {
  const entry = s.pending.get(params[0])
  if (!entry) {
    const known = s.data.statements.find((st) => st.id === params[0])
    return known ? json(known) : fail('notFound', 'error.notFound')
  }
  // First poll returns `parsing`; the next one resolves. Keeps the upload
  // screen's parsing state real rather than a hardcoded delay.
  if (entry.statement.status === 'parsing') {
    entry.statement = { ...entry.statement, status: 'needsReview' }
  }
  return json(entry.statement)
})

on('GET', '/statements/{id}/preview', ({ s, params }) => {
  const entry = s.pending.get(params[0])
  if (!entry) return fail('notFound', 'error.notFound')
  if (entry.statement.failureReason === 'passwordProtected') {
    return fail('passwordProtected', 'importReview.locked.title')
  }
  return json({ statement: entry.statement, transactions: entry.transactions })
})

on('POST', '/statements/{id}/unlock', async ({ s, params, body }) => {
  const entry = s.pending.get(params[0])
  if (!entry) return fail('notFound', 'error.notFound')
  const { password } = (await body()) as { password?: string }
  if (password !== entry.password) return fail('passwordProtected', 'importReview.locked.error')
  entry.statement = { ...entry.statement, status: 'needsReview', failureReason: null }
  return json(entry.statement)
})

on('POST', '/statements/{id}/confirm', async ({ s, params, body }) => {
  const entry = s.pending.get(params[0])
  if (!entry) return fail('notFound', 'error.notFound')
  const { accountId, newAccount } = (await body()) as {
    accountId?: string | null
    newAccount?: { name: string; type: Account['type'] } | null
  }

  const existing = s.data.accounts.find((a) => a.id === accountId)
  if (!existing && !newAccount?.name) {
    return fail('validationFailed', 'error.generic', { field: 'accountId' })
  }

  const account: Account = existing ?? {
    id: uuid(`account:new:${newAccount!.name}`),
    name: newAccount!.name,
    institution: entry.statement.detected?.institution ?? null,
    type: newAccount!.type,
    mask: entry.statement.detected?.mask ?? null,
    currency: CURRENCY,
    currentBalance: entry.statement.closingBalance,
    availableBalance: entry.statement.closingBalance,
    aprBasisPoints: null,
    statementDayOfMonth: 1,
    coverage: {
      from: entry.statement.periodStart ?? null,
      to: entry.statement.periodEnd ?? null,
      gaps: [],
    },
    transactionCount: 0,
    statementCount: 0,
    isDebt: newAccount!.type === 'creditCard' || newAccount!.type === 'loan',
    createdAt: new Date().toISOString(),
  }
  if (!existing) s.data.accounts.push(account)

  const skipped = entry.statement.duplicateCount ?? 0
  const imported = entry.transactions.slice(skipped).map((tx) => ({ ...tx, accountId: account.id }))
  s.data.transactions = [...imported, ...s.data.transactions].sort((a, b) => (a.date < b.date ? 1 : -1))
  account.transactionCount = (account.transactionCount ?? 0) + imported.length
  account.statementCount = (account.statementCount ?? 0) + 1

  const statement: Statement = {
    ...entry.statement,
    status: 'imported',
    accountId: account.id,
    importedAt: new Date().toISOString(),
  }
  s.data.statements = [statement, ...s.data.statements]
  s.pending.set(params[0], { ...entry, statement })

  return json({
    statement,
    account,
    importedCount: imported.length,
    skippedCount: skipped,
  } satisfies ConfirmImportResponse)
})

on('DELETE', '/statements/{id}', ({ s, params }) => {
  s.pending.delete(params[0])
  s.data.statements = s.data.statements.filter((st) => st.id !== params[0])
  s.data.transactions = s.data.transactions.filter((t) => t.statementId !== params[0])
  return noContent()
})

on('GET', '/accounts', ({ s }) => json(s.data.accounts))

on('GET', '/accounts/{id}', ({ s, params }) => {
  const account = s.data.accounts.find((a) => a.id === params[0])
  return account ? json(account) : fail('notFound', 'error.notFound')
})

on('GET', '/accounts/{id}/statements', ({ s, params }) =>
  json(
    s.data.statements
      .filter((st) => st.accountId === params[0])
      .sort((a, b) => ((a.periodEnd ?? '') < (b.periodEnd ?? '') ? 1 : -1)),
  ),
)

on('PATCH', '/accounts/{id}', async ({ s, params, body }) => {
  const account = s.data.accounts.find((a) => a.id === params[0])
  if (!account) return fail('notFound', 'error.notFound')
  Object.assign(account, await body())
  return json(account)
})

on('DELETE', '/accounts/{id}', ({ s, params }) => {
  s.data.accounts = s.data.accounts.filter((a) => a.id !== params[0])
  s.data.transactions = s.data.transactions.filter((t) => t.accountId !== params[0])
  return noContent()
})

const PAGE_LIMIT = 50

on('GET', '/transactions', ({ s, url }) => {
  const q = url.searchParams
  const data = withOverrides(s)
  let rows = data.transactions

  const accountId = q.get('accountId')
  if (accountId) rows = rows.filter((t) => t.accountId === accountId)
  const category = q.get('category')
  if (category) rows = rows.filter((t) => t.category === category)
  const from = q.get('from')
  if (from) rows = rows.filter((t) => t.date >= from)
  const to = q.get('to')
  if (to) rows = rows.filter((t) => t.date <= to)
  const text = q.get('q')?.trim().toLowerCase()
  if (text) {
    rows = rows.filter(
      (t) =>
        t.description.toLowerCase().includes(text) || (t.merchant ?? '').toLowerCase().includes(text),
    )
  }
  if (q.get('recurring') === 'true') rows = rows.filter((t) => t.isRecurring)
  if (q.get('fees') === 'true') rows = rows.filter((t) => t.isFee)
  if (q.get('uncategorized') === 'true') rows = rows.filter((t) => t.category === 'uncategorized')
  if (q.get('direction') === 'inflow') rows = rows.filter((t) => t.amount.amount > 0 && !t.isTransfer)
  if (q.get('direction') === 'outflow') rows = rows.filter((t) => t.amount.amount < 0 && !t.isTransfer)

  const limit = Math.min(Number(q.get('limit')) || PAGE_LIMIT, 200)
  const cursor = Number(q.get('cursor') ?? 0)
  const page = rows.slice(cursor, cursor + limit)
  const next = cursor + limit < rows.length ? String(cursor + limit) : null
  return json({ items: page, nextCursor: next })
})

on('GET', '/transactions/{id}', ({ s, params }) => {
  const tx = withOverrides(s).transactions.find((t) => t.id === params[0])
  return tx ? json(tx) : fail('notFound', 'error.notFound')
})

on('PATCH', '/transactions/{id}', async ({ s, params, body }) => {
  const tx = withOverrides(s).transactions.find((t) => t.id === params[0])
  if (!tx) return fail('notFound', 'error.notFound')
  const patch = (await body()) as Partial<Transaction> & { applyToMerchant?: boolean }
  const { applyToMerchant, ...fields } = patch

  const apply = (id: string) =>
    s.transactionOverride.set(id, {
      ...(s.transactionOverride.get(id) ?? {}),
      ...fields,
      categorySource: fields.category ? 'user' : undefined,
      updatedAt: new Date().toISOString(),
    })

  if (applyToMerchant && tx.merchantKey) {
    for (const other of s.data.transactions.filter((t) => t.merchantKey === tx.merchantKey)) apply(other.id)
  } else {
    apply(tx.id)
  }
  return json({ ...tx, ...s.transactionOverride.get(tx.id) })
})

on('GET', '/analysis/score', ({ s }) => json(withScoreNarrative(analysis(s).score, s.generation)))

on('GET', '/analysis/status', ({ s }) => {
  s.statusPolls += 1
  const score = analysis(s).score
  return json({
    deterministic: {
      state: 'ready',
      completedAt: score.computedAt,
      monthsOfData: score.monthsOfData ?? 0,
    },
    generation: generationStatus(s.generation, score.monthsOfData ?? 0, s.statusPolls),
  })
})

// 202 and nothing else: the prose arrives on a later status read, not here.
on('POST', '/analysis/explain', ({ s }) => {
  if (s.generation === 'failed') s.generation = 'ready'
  s.statusPolls = 0
  return new Response(null, { status: 202 })
})

on('GET', '/analysis/cash-flow', ({ s, url }) =>
  json(deriveCashFlow(withOverrides(s), Number(url.searchParams.get('months')) || 12)),
)

on('GET', '/analysis/recurring', ({ s, url }) => {
  const status = url.searchParams.get('status')
  // Series below 0.6 confidence are withheld until the user confirms them.
  const all = analysis(s).recurring.filter((r) => (r.confidence ?? 0) >= 0.6 || r.userConfirmed)
  return json(status ? all.filter((r) => r.status === status) : all)
})

on('GET', '/analysis/recurring/{id}', ({ s, params }) => {
  const series = analysis(s).recurring.find((r) => r.id === params[0])
  return series ? json(series) : fail('notFound', 'error.notFound')
})

on('PATCH', '/analysis/recurring/{id}', async ({ s, params, body }) => {
  const patch = (await body()) as Partial<RecurringSeries>
  s.recurringOverride.set(params[0], { ...(s.recurringOverride.get(params[0]) ?? {}), ...patch })
  const series = analysis(s).recurring.find((r) => r.id === params[0])
  return series ? json(series) : fail('notFound', 'error.notFound')
})

on('GET', '/analysis/fees', ({ s, url }) =>
  json(deriveFees(withOverrides(s), Number(url.searchParams.get('months')) || 12)),
)

on('GET', '/analysis/debt', ({ s }) => json(deriveDebt(withOverrides(s))))

on('POST', '/analysis/debt/plan', async ({ s, body }) => {
  const { monthlyPayment, strategy } = (await body()) as {
    monthlyPayment: Money
    strategy: DebtPlan['strategy']
  }
  return json(buildPlan(withOverrides(s), monthlyPayment, strategy))
})

on('PUT', '/analysis/debt/plan', async ({ s, body }) => {
  s.savedPlan = { ...((await body()) as DebtPlan), isSaved: true }
  return json(s.savedPlan)
})

on('POST', '/analysis/refresh', () => new Response(null, { status: 202 }))

on('GET', '/recommendations', ({ s, url }) => {
  const theme = url.searchParams.get('theme')
  const status = url.searchParams.get('status')
  let rows = analysis(s).recommendations
  if (theme === 'quickWins') rows = rows.filter((r) => r.effort === 'low')
  else if (theme) rows = rows.filter((r) => r.theme === theme)
  rows = status ? rows.filter((r) => r.status === status) : rows
  return json(withRecommendationProse(rows, s.generation))
})

on('GET', '/recommendations/{id}', ({ s, params }) => {
  const rows = withRecommendationProse(analysis(s).recommendations, s.generation)
  const rec = rows.find((r) => r.id === params[0])
  return rec ? json(rec) : fail('notFound', 'error.notFound')
})

on('PATCH', '/recommendations/{id}', async ({ s, params, body }) => {
  const { status } = (await body()) as { status: Recommendation['status'] }
  s.recommendationStatus.set(params[0], status)
  const rec = analysis(s).recommendations.find((r) => r.id === params[0])
  return rec ? json(rec) : fail('notFound', 'error.notFound')
})

on('GET', '/progress', ({ s, url }) => {
  const range = (url.searchParams.get('range') ?? '12m') as Progress['range']
  const a = analysis(s)
  return json(
    withProgressNarrative(
      deriveProgress(range, a.score, a.cashFlow, a.recurring, a.recommendations, deriveDebt(a.data)),
      s.generation,
    ),
  )
})

on('GET', '/settings', ({ s }) => json(s.settings))

on('PATCH', '/settings', async ({ s, body }) => {
  s.settings = { ...s.settings, ...(await body()) }
  if (s.settings.emergencyFundTarget) s.data.emergencyFundTarget = s.settings.emergencyFundTarget
  return json(s.settings)
})

on('POST', '/export', async ({ s, body }) => {
  const { format, scope } = (await body()) as { format: 'csv' | 'json'; scope: string }
  const rows = withOverrides(s).transactions
  const payload =
    format === 'json'
      ? JSON.stringify(rows, null, 2)
      : [
          'date,account,description,merchant,category,amount,currency',
          ...rows.map((t) =>
            [
              t.date,
              t.accountId,
              JSON.stringify(t.description),
              JSON.stringify(t.merchant ?? ''),
              t.category,
              t.amount.amount,
              t.amount.currency,
            ].join(','),
          ),
        ].join('\n')
  const blob = new Blob([payload], { type: format === 'json' ? 'application/json' : 'text/csv' })
  return json({
    url: URL.createObjectURL(blob),
    expiresAt: new Date(Date.now() + 3_600_000).toISOString(),
    // The filename is not part of the contract; the client derives it.
    scope,
  })
})

// ---------------------------------------------------------------------- plaid

/**
 * A stand-in for the provider. It mints sentinel tokens that lib/plaid.ts
 * recognizes instead of opening the real Link UI, so the link flow is walkable
 * with no provider credentials and no network. Nothing here models a real
 * access token, because no client is ever supposed to see one.
 */
const INSTITUTIONS = ['Northgate Savings', 'Riverbend Credit Union', 'Halcyon Bank']

function linkTokenFor(mode: PlaidMode): string {
  if (mode === 'cancel') return 'mock-link-cancelled'
  if (mode === 'fail') return 'mock-link-failed'
  return 'mock-link-success'
}

const linkTokenResponse = (mode: PlaidMode) =>
  json({
    linkToken: linkTokenFor(mode),
    expiration: new Date(Date.now() + 30 * 60_000).toISOString(),
  })

/** api.json: unconfigured provider is serverError + linkAccount.error.unavailable. */
const unavailable = () =>
  json(
    {
      code: 'serverError',
      copyKey: 'linkAccount.error.unavailable',
      retryable: false,
    } satisfies ApiError,
    500,
  )

on('POST', '/plaid/link-token', () => {
  const mode = plaidModeFromUrl()
  return mode === 'off' ? unavailable() : linkTokenResponse(mode)
})

on('POST', '/plaid/items/{id}/relink', ({ s, params }) => {
  const mode = plaidModeFromUrl()
  if (mode === 'off') return unavailable()
  if (!s.connections.some((c) => c.id === params[0])) return fail('notFound', 'error.notFound')
  return linkTokenResponse(mode)
})

on('POST', '/plaid/exchange', async ({ s, body }) => {
  const mode = plaidModeFromUrl()
  if (mode === 'off') return unavailable()
  const { publicToken } = (await body()) as { publicToken?: string }
  if (!publicToken) return fail('validationFailed', 'linkAccount.error.title', { field: 'publicToken' })

  const index = s.connections.length
  const institutionName = INSTITUTIONS[index % INSTITUTIONS.length]
  const now = new Date().toISOString()

  // One new account per connection, joining the same pool statements write to.
  const account: Account = {
    id: uuid(`account:linked:${institutionName}:${index}`),
    name: `${institutionName} Checking`,
    institution: institutionName,
    type: 'checking',
    mask: String(4400 + index).slice(-4),
    currency: CURRENCY,
    currentBalance: money(248_00),
    availableBalance: money(248_00),
    aprBasisPoints: null,
    statementDayOfMonth: 1,
    coverage: { from: null, to: null, gaps: [] },
    transactionCount: 0,
    statementCount: 0,
    isDebt: false,
    createdAt: now,
  }
  s.data.accounts.push(account)

  const connection: BankConnection = {
    id: uuid(`connection:${institutionName}:${index}`),
    institutionName,
    institutionId: `ins_mock_${index}`,
    logoUrl: null,
    // Starts syncing so linkSuccess and connectedAccounts both show a real
    // syncing state before it settles; see the GET handler below.
    status: 'syncing',
    accountIds: [account.id],
    lastSyncedAt: null,
    lastSyncTransactionCount: 0,
    consentExpiresAt: null,
    errorCode: null,
    createdAt: now,
  }
  s.connections.push(connection)

  return json({ connection, accounts: [account] })
})

/** Settles a syncing connection on the next poll, so the state is observable. */
function settle(connection: BankConnection): BankConnection {
  if (connection.status !== 'syncing') return connection
  const settled: BankConnection = {
    ...connection,
    status: 'active',
    lastSyncedAt: new Date().toISOString(),
    lastSyncTransactionCount: 18,
  }
  return settled
}

on('GET', '/plaid/items', ({ s }) => {
  s.connections = s.connections.map(settle)
  return json(s.connections)
})

on('GET', '/plaid/items/{id}', ({ s, params }) => {
  const index = s.connections.findIndex((c) => c.id === params[0])
  if (index < 0) return fail('notFound', 'error.notFound')
  s.connections[index] = settle(s.connections[index])
  return json(s.connections[index])
})

on('POST', '/plaid/items/{id}/sync', ({ s, params }) => {
  const connection = s.connections.find((c) => c.id === params[0])
  if (!connection) return fail('notFound', 'error.notFound')
  connection.status = 'syncing'
  return new Response(null, { status: 202 })
})

on('DELETE', '/plaid/items/{id}', ({ s, params }) => {
  const connection = s.connections.find((c) => c.id === params[0])
  if (!connection) return fail('notFound', 'error.notFound')
  // Transactions already imported are KEPT — only the link goes away.
  s.connections = s.connections.filter((c) => c.id !== params[0])
  return noContent()
})

on('DELETE', '/me/statements', ({ s }) => {
  s.data.statements = []
  s.pending.clear()
  return noContent()
})

on('DELETE', '/me', ({ s }) => {
  s.data = makeDataset('empty')
  s.pending.clear()
  s.recommendationStatus.clear()
  s.recurringOverride.clear()
  s.transactionOverride.clear()
  s.savedPlan = null
  s.connections = []
  s.authenticated = false
  return noContent()
})

// ------------------------------------------------------------------ transport

/** Everything a caller with no session is allowed to reach — `auth: public`
 *  in product/api.json. /auth/methods has to be here or the screen that asks
 *  what to render cannot ask before signing in. */
const PUBLIC_PATHS = [
  '/auth/methods',
  '/auth/code',
  '/auth/verify',
  '/auth/password',
  '/beta-notifications',
]

async function handle(request: Request): Promise<Response> {
  const url = new URL(request.url, location.origin)
  const path = url.pathname.replace(/^\/api\/v1/, '')
  const s = store()

  // Before routing, and before the session gate: the relay refuses at the edge,
  // so it never learns whether the path exists or who was asking.
  const refused = edgeRefusal(path)
  if (refused) {
    await new Promise((resolve) => setTimeout(resolve, 60))
    return refused
  }

  for (const route of routes) {
    if (route.method !== request.method) continue
    const match = route.pattern.exec(path)
    if (!match) continue

    if (!s.authenticated && !PUBLIC_PATHS.includes(path)) {
      return fail('unauthorized', 'error.session')
    }

    // A little latency so loading states are actually visible in dev.
    await new Promise((resolve) => setTimeout(resolve, 90 + Math.random() * 160))

    const cloned = request.clone()
    return route.handler({
      s,
      params: match.slice(1).map(decodeURIComponent),
      url,
      body: async () => {
        const text = await cloned.text()
        return text ? (JSON.parse(text) as Record<string, unknown>) : {}
      },
      form: () => cloned.formData(),
    })
  }

  bridge.log(`mock: no route for ${request.method} ${path}`)
  return fail('notFound', 'error.notFound')
}

let installed = false

export function installMock(): void {
  if (!import.meta.env.DEV || installed) return
  installed = true
  state = init()
  setTransport(handle)
  bridge.log(
    'mock backend installed — the Server surface is not answering on :6700. Add ?dataset=empty or ?dataset=thin to exercise the empty and insufficientData states.',
  )
  console.warn('Fintech: using the built-in mock backend. Start the Server surface on :6700 for real data.')
}

export function isMockInstalled(): boolean {
  return installed
}

/** Lets the sign-in screen mark the mock session live without a round trip. */
export function markMockAuthenticated(email: string): void {
  if (!installed) return
  const s = store()
  s.authenticated = true
  s.settings = { ...s.settings, email }
}

/** Walk the server-owned Google result branch without leaving the dev SPA. */
export function completeMockGoogleSignIn(): 'success' | 'betaAccessRequired' {
  const beta = new URLSearchParams(location.search).get('beta')
  if (beta) return 'betaAccessRequired'
  markMockAuthenticated('google@example.com')
  return 'success'
}
